// Fog state shared by the land layer and the sea veil: the field in the overlay texture, and the
// reveal animation that folds fresh data back over ~1 s.
//
//   setSnapshot(field) ──► prev = what is on screen now, now = new field, reveal 0
//   every frame ........... reveal 0 → 1 (eased); the shaders dissolve prev → now per pixel
//
// The first field only appears (nothing to fold back from), and an unchanged one does nothing.

import { uniform } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import type { WorldSnapshot } from '../../world/api.ts';
import { HEX_COUNT } from '../../core/grid.ts';
import { blendField, buildFogField } from './fogField.ts';
import { CHANNEL, type OverlayData } from './overlayData.ts';
import { FOG_FROM } from './parchment.ts';
import { approach, ease } from './tween.ts';

/** Seconds a changed field takes to fold in. */
const REVEAL_S = 1.1;

export interface FogState {
  data: OverlayData;
  /** 0..1 uniform: how far the field has moved from `prev` to `now`. */
  reveal: Node<'float'>;
  /** True while a field is folding in. */
  animating(): boolean;
  /** True when any hex with no owner carries fog (the sea veil has something to draw). */
  hasSea(): boolean;
  setSnapshot(snapshot: WorldSnapshot): void;
  update(dt: number): void;
}

export function createFogState(data: OverlayData): FogState {
  const revealU = uniform(1);
  let now: Uint8Array | null = null;
  let prev: Uint8Array | null = null;
  let raw = 1;
  let sea = false;

  function same(a: Uint8Array, b: Uint8Array): boolean {
    for (let i = 0; i < HEX_COUNT; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  function hasSeaFog(owner: Uint16Array, field: Uint8Array): boolean {
    for (let i = 0; i < HEX_COUNT; i++) {
      if (owner[i] === 0 && field[i] >= FOG_FROM) {
        return true;
      }
    }
    return false;
  }

  return {
    data,
    reveal: revealU as unknown as Node<'float'>,
    animating: () => raw < 1,
    hasSea: () => sea,
    setSnapshot(snapshot) {
      const field = buildFogField(snapshot.staleness, snapshot.owner);
      sea = hasSeaFog(snapshot.owner, field);
      if (!now || !prev) {
        now = field;
        prev = field;
        data.write(CHANNEL.FOG, field);
        data.write(CHANNEL.FOG_PREV, field);
        revealU.value = raw = 1;
        return;
      }
      if (same(now, field)) {
        return;
      }

      // Fold from what the screen shows now, so a snapshot that lands mid-fold does not jump.
      prev = raw < 1 ? blendField(prev, now, ease(raw)) : now;
      now = field;
      data.write(CHANNEL.FOG_PREV, prev);
      data.write(CHANNEL.FOG, field);
      revealU.value = raw = 0;
    },
    update(dt) {
      if (raw >= 1) {
        return;
      }
      raw = approach(raw, 1, dt, REVEAL_S);
      revealU.value = ease(raw);
    },
  };
}
